import React from "react";
import { Box, chakra, Flex, Icon, Text } from "@chakra-ui/react";
import { FiCheckCircle, FiUploadCloud } from "react-icons/fi";

export interface UploadTileProps {
  label: string;
  /** Name of the chosen (or previously saved) file; undefined until one exists. */
  fileName?: string;
  onClick: () => void;
  isDisabled?: boolean;
  /** Caption shown until a file is attached (default "PDF or JPG"). */
  hint?: string;
}

/**
 * Click-to-browse tile that opens a hidden file input. A real button so it is keyboard operable;
 * turns leaf-tinted once a file is attached. Shared by both application wizards' Documents step.
 * @example <UploadTile label="Upload trade licence" fileName={file?.name} onClick={() => inputRef.current?.click()} />
 */
export const UploadTile: React.FC<UploadTileProps> = ({
  label,
  fileName,
  onClick,
  isDisabled = false,
  hint = "PDF or JPG",
}) => {
  const hasFile = !!fileName;
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      w="full"
      display="flex"
      alignItems="center"
      gap={3}
      textAlign="left"
      px={4}
      py={3}
      borderRadius="md"
      border="1px dashed"
      borderColor={hasFile ? "leaf.500" : "border.strong"}
      bg={hasFile ? "leaf.50" : "surface.card"}
      color="text.primary"
      transition="border-color 160ms cubic-bezier(0.2,0.8,0.2,1), background-color 160ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 160ms cubic-bezier(0.2,0.8,0.2,1)"
      _hover={{
        borderColor: "leaf.500",
        bg: "leaf.50",
        _disabled: { borderColor: "border.default", bg: "surface.card" },
      }}
      _focusVisible={{ boxShadow: "outline", borderColor: "navy.900" }}
      _disabled={{
        cursor: "not-allowed",
        opacity: 0.5,
        borderColor: "border.default",
        bg: "surface.card",
      }}
    >
      <Flex
        as="span"
        display="flex"
        w="36px"
        h="36px"
        flexShrink={0}
        borderRadius="full"
        bg={hasFile ? "leaf.100" : "navy.50"}
        color={hasFile ? "leaf.700" : "navy.700"}
        align="center"
        justify="center"
        aria-hidden
      >
        <Icon as={hasFile ? FiCheckCircle : FiUploadCloud} boxSize="18px" />
      </Flex>
      <Box as="span" display="block" minW={0}>
        <Text as="span" display="block" textStyle="body.sm" fontWeight={700} noOfLines={1}>
          {label}
        </Text>
        <Text
          as="span"
          display="block"
          textStyle="caption"
          color={hasFile ? "leaf.800" : "text.muted"}
          noOfLines={1}
          title={fileName}
        >
          {fileName ?? hint}
        </Text>
      </Box>
    </chakra.button>
  );
};
